import capitalize from 'lodash/capitalize'
import keyBy from 'lodash/keyBy'
import pick from 'lodash/pick'
import sortBy from 'lodash/sortBy'

const { api } = useFeathers()
const { getParties } = useStatic()
const { calcTotal } = useCalculator()
const { snackbarAction } = useSnackbar()

export const useOrderStore = defineStore('order', {
  state: () => ({
    activeTab: 'product',
    backsideText: {
      line1: '',
      line2: '',
      line3: '',
      line4: '',
      line5: '',
      voterFriendly: false,
    },
    election: null as any,
    defaultBackside: {
      line1: '',
      line2: '',
      line3: '',
      line4: '',
      line5: '',
      voterFriendly: false,
    },
    files: null as any,
    grouping: null,
    jobNumber: '',
    loading: true,
    matrixes: [],
    options: {
      backside: false,
      ballotCreation: false,
      ballotType: null,
      language: null,
      scannable: true,
      size: null,
      stub: false,
      testApproval: false,
    },
    orderId: null as any,
    orderItem: null as any,
    orderItemQtys: [] as any,
    party: undefined,
    product: undefined as undefined | any,
    quantities: [] as any[],
    stub: {
      checkbox1: null as any,
      checkbox2: null as any,
      index: 0,
    } as any,
  }),
  getters: {
    isEdit(state) {
      return !!state.orderItem
    },
    isMabs(state) {
      return state.product?.FileType === 'MABS'
    },
    isValid(state) {
      const tabs: Record<string, boolean> = {
        product: !!state.product,
        quantity: this.totalQuantities.items + this.totalQuantities.samples + this.totalQuantities.tests > 0,
        options: ((state.product?.FileType === 'MABS' && state.options?.ballotType) || state.product?.FileType !== 'MABS')
          && (!(state.product?.ItemType === 'Material' && !state.options.size)),
      }
      return tabs?.[state.activeTab] || false
    },
    showOptionsTab(state) {
      return state.product?.Stub || state.product?.Backside || state.product?.Multilingual || state.product?.Size || state.product?.BallotCreation || state.product?.Scannable
    },
    totalQuantities(state): Record<string, number> {
      return {
        items: calcTotal(state.quantities, 'Items', null),
        samples: calcTotal(state.quantities, 'Samples', null),
        tests: calcTotal(state.quantities, 'Tests', null),
      }
    },
  },
  actions: {
    async addProduct(cart: boolean) {
      const { orderId, cartDrawer } = storeToRefs(useCartStore())
      const id = cart ? orderId.value : this.orderId as number

      const productPayload = this.genProductPayload(id)

      const errorMsg = `Error adding product ${cart ? 'cart' : 'order'}`
      const successMsg = `Product added to ${cart ? 'cart' : 'order'}`
      await snackbarAction(async () => {
        if (cart) {
          await api.service('pge/orders').patch(id, {
            CustomerAccount: this.election?.CustomerAccount,
            ElectionID: this.election?.ID || 0,
          })
        }
        const orderProduct = await api.service('pge/orderProducts').create(productPayload)
        // set product id on qtys
        const quantityPayload = await this.genQuantityPayload(orderProduct.ID as any)
        await api.service('pge/orderQtys').create(quantityPayload)

        this.$reset()
        if (cart)
          cartDrawer.value = true
        else api.service('cc/epmsComponent').create({ Message: orderProduct.ID })
      }, successMsg, errorMsg)
    },
    calcEstimatedCost() {
      const { user } = storeToRefs(useAuthStore()) as any
      const matrixByCounty = keyBy(this.product?.EstimatedCosts?.filter((v: any) =>
        this.product?.ItemType === 'Material' || (
          v.Scannable === this.options.scannable && v.BallotCreation === this.options.ballotCreation && v.Stub === this.options.stub
        )), 'County') as any
      const matrixGroup = user?.value?.SelectedCustomer?.County?.toLowerCase() || 'all'
      const matrix = matrixByCounty?.[matrixGroup] || matrixByCounty?.all || {}
      return Object.keys(this.totalQuantities).reduce((acc: any, key: string) => {
        const type = capitalize(key.slice(0, -1)) || null
        if (['General', 'Primary'].includes(this.election?.Type) || ['voters'].includes(key) || this.totalQuantities[key] <= 0)
          return acc
        const qty = this.totalQuantities[key]
        const minQty = matrix?.[`${type}MinQty`] || 0
        const minPrice = matrix?.[`${type}MinPrice`] || 0
        const pricePer = matrix?.[`${type}PricePer`] || 0
        const cost = (minPrice + (qty > minQty
          ? (qty - minQty) * pricePer
          : 0))
        acc[`${type}Cost`] = !acc[`${type}Cost`]
          ? cost || 0
          : acc[`${type}Cost`] + cost
        acc.TotalCost = !acc.TotalCost
          ? cost || 0
          : acc.TotalCost + cost
        return acc
      }, {
        ItemCost: 0,
        SampleCost: 0,
        TestCost: 0,
        TotalCost: 0,
      })
    },
    genProductPayload(id: number, patch = false): any {
      const isBallot = this.product?.ItemType === 'Ballot' && this.product?.FileType !== 'MABS'
      return {
        ...(!patch) && {
          OrderID: id,
          ProductID: this.product?.ID,
          ItemType: this.product?.ItemType || null,
          FileType: `${isBallot
            ? (this.options?.language as any)?.charAt(0) || ''
            : ''}${this.product?.FileType || ''}` || null,
          RequestFiles: false,
        },
        Description: [
          this.options?.size
            ? this.product?.Description === 'Envelopes'
              ? `#${this.options?.size}`
              : `${this.options?.size}"`
            : '',
          ...(this.options?.language
            ? [this.options.language]
            : []),
          this.product?.Description,
          this.product?.FileType === 'MABS' && this.options?.ballotType
            ? `- ${this.options?.ballotType}`
            : '',
        ].join(' ').trim(),
        ItemQuantity: this.totalQuantities?.items || 0,
        SampleQuantity: this.totalQuantities?.samples || 0,
        TestQuantity: this.totalQuantities?.tests || 0,
        ...this.calcEstimatedCost(),
        Party: this.party || null,
        BySheet: this.grouping === 'Sheet',
        Backside: this.options?.backside || false,
        BacksideText: this.options?.backside
          ? this.backsideText
          : {},
        BallotCreation: this.options?.ballotCreation || false,
        BallotType: isBallot
          ? this.product?.FileType || null
          : this.options?.ballotType || null,
        Checkbox1: this.options?.stub
          ? this.stub?.checkbox1 || null
          : null,
        Checkbox2: this.options?.stub
          ? this.stub?.checkbox2 || null
          : null,
        Language: this.options?.language || null,
        Scannable: Object.hasOwn(this.options, 'scannable')
          ? this.options.scannable
          : true,
        Size: this.options?.size || null,
        Stub: this.options?.stub || false,
        TestApproval: !!this.options?.testApproval,
      }
    },
    async genQuantityPayload(productID: number) {
      const payload = []
      for (const row of this.quantities) {
        if ((Number.parseInt(row.Items) + Number.parseInt(row.Samples) + Number.parseInt(row.Tests)) === 0)
          continue
        const qty = {
          OrderProductID: productID,
          ElectionMatrixID: row.ElectionMatrixID,
          Items: Number.parseInt(row.Items),
          Samples: Number.parseInt(row.Samples),
          Tests: Number.parseInt(row.Tests),
          StubMunicipality: this.product.Stub ? row.StubMunicipality : null,
          StubElectionName: this.product.Stub ? row.StubElectionName : null,
          StubLine2: this.product.Stub ? row.StubLine2 : null,
          StubDistrictInfo1: this.product.Stub ? row.StubDistrictInfo1 : null,
          StubDistrictInfo2: this.product.Stub ? row.StubDistrictInfo2 : null,
          StubDistrictInfo3: this.product.Stub ? row.StubDistrictInfo3 : null,
        }
        payload.push(qty)
      }
      return payload
    },
    resetOrder() {
      // delay clear till transition finishes
      setTimeout(() => this.$reset(), 200)
    },
    setBackside() {
      this.$patch({
        backsideText: { ...this.defaultBackside },
      })
    },
    setQuantities() {
      this.$patch({ loading: true })

      const electionType = this.election?.Type || ''
      const customer = (useAuthStore() as any).user?.SelectedCustomer?.CustName || null
      const county = (useAuthStore() as any).user?.SelectedCustomer?.County || null
      const lastStub = this.election?.LastStub || {}
      const defaultData = {
        // ID: null,
        ElectionMatrixID: null,
        CustomerAccount: '',
        DistrictKey: '',
        DistrictName: customer,
        Subdivision: '',
        ElectionDistrict: 1,
        ED: 1,
        Party: '',
        BySheet: false,
        Sheet: 1,
        Type: '',
        Voters: 0,
        Items: 0,
        Samples: 0,
        Tests: 0,
        StubMunicipality: lastStub?.StubMunicipality || null,
        StubElectionName: lastStub?.StubElectionName || null,
        StubLine2: lastStub?.StubLine2 || null,
        StubDistrictInfo1: lastStub?.StubDistrictInfo1 || customer || null,
        StubDistrictInfo2: lastStub?.StubDistrictInfo2 || null,
        StubDistrictInfo3: lastStub?.StubDistrictInfo3 || null,
      }
      const useDefaultQty = ['Deck', 'Material'].includes(this.product?.ItemType || '') || this.product?.FileType === 'MABS'
      const orderQtysByMatrix = keyBy(this.orderItemQtys, 'ElectionMatrixID')
      const sheetKey = !this.product.FileType || this.product.FileType === 'EDB' || !['Abs', 'Aff', 'Mil'].includes(this.product.FileType)
        ? 'AbsSheet'
        : `${capitalize(this.product.FileType)}Sheet`

      const GenQtys = () => {
        // if primary/general, generate bySheet Lines
        const qtys = this.orderItemQtys?.length
          ? this.orderItemQtys
          : this.matrixes.length && !useDefaultQty
            ? this.matrixes
            : [defaultData]
        return this.grouping === 'Sheet' && ['General', 'Primary'].includes(electionType)
          ? Object.values(qtys.reduce((acc: any, val: any) => {
              const itemParty = this.isEdit ? val?.Matrix?.Party : val?.Party
              const matrix = this.isEdit ? val?.Matrix : val
              const hasConsolidation = !!matrix?.ConsVoters || matrix?.ConsVoters === 0
              if (electionType === 'Primary' && (!this.party || this.party !== itemParty || itemParty === null))
                return acc
              const sheet = this.isEdit
                ? val?.Matrix?.[sheetKey]
                : val?.[sheetKey] || 13
              if (!acc[sheet]) {
                acc[sheet] = genQtyLine(val)
                acc[sheet].DistrictName = customer
                acc[sheet].StubDistrictInfo1 = ''
                acc[sheet].StubDistrictInfo2 = ''
                acc[sheet].StubDistrictInfo3 = ''
                acc[sheet].BySheet = true
              }
              else {
                const voterQty = (hasConsolidation ? matrix.ConsVoters : matrix.Voters) || 0
                acc[sheet].Voters += Number.parseInt(voterQty)
              }
              return acc
            }, {}))
          : qtys.filter((val: any) => {
              return !this.party || this.party === (this.isEdit ? val?.Matrix?.Party : val?.Party)
            }).map((val: any) => genQtyLine(val))
      }

      const genQtyLine = (qtyLine: any) => {
        const isGeneralPrimary = ['General', 'Primary'].includes(electionType)
        const matrix = {
          ...qtyLine,
          ...(this.matrixes.length && !useDefaultQty)
            ? {
                ...orderQtysByMatrix?.[qtyLine.ElectionMatrixID] || {},
                ...(this.isEdit) && {
                  ...qtyLine?.Matrix || {},
                },
                ElectionMatrixID: (this.isEdit ? qtyLine.ElectionMatrixID : qtyLine.ID) || null,
                ElectionDistrict: ['OH', 'PA'].includes(this.election?.Customer?.State)
                  ? qtyLine?.Matrix?.ED || qtyLine.ED
                  : qtyLine?.Matrix?.ElectionDistrict || qtyLine.ElectionDistrict,
              }
            : pick(defaultData, [
                'DistrictName',
                'Subdivision',
                'ElectionDistrict',
                'Voters',
              ]),
        }
        const hasConsolidation = !!matrix?.ConsVoters || matrix?.ConsVoters === 0
        const districtInfo = {
          DistrictName: hasConsolidation ? matrix?.ConsDistrictName : matrix?.DistrictName,
          Subdivision: hasConsolidation ? matrix?.ConsSubdivision : matrix?.Subdivision,
          ElectionDistrict: hasConsolidation ? matrix?.ConsElectionDistrict : matrix?.ElectionDistrict,
          Voters: hasConsolidation ? matrix?.ConsVoters : matrix?.Voters,
        }

        const stub = {
          StubMunicipality: (isGeneralPrimary ? county : lastStub?.StubMunicipality) || null,
          StubElectionName: (['General'].includes(electionType)
            ? this.election?.Description?.split(' - ')[0] || 'General Election'
            : ['Primary'].includes(electionType)
                ? getParties()[this.party || 'Default']
                : lastStub?.StubElectionName) || null,
          StubLine2: (isGeneralPrimary ? '' : lastStub?.StubLine2) || null,
          StubDistrictInfo1: (isGeneralPrimary
            ? districtInfo.DistrictName
            : lastStub?.StubDistrictInfo1 || customer) || null,
          StubDistrictInfo2: (isGeneralPrimary ? districtInfo.Subdivision : lastStub?.StubDistrictInfo2) || null,
          StubDistrictInfo3: (isGeneralPrimary ? districtInfo.ElectionDistrict : lastStub?.StubDistrictInfo3) || null,
        }

        return {
          ...defaultData,
          DistrictKey: this.isEdit
            ? qtyLine.Matrix.DistrictKey
            : qtyLine.DistrictKey || '',
          ID: qtyLine.ID,
          Party: this.isEdit
            ? qtyLine.Matrix.Party
            : qtyLine.Party || '',
          Sheet: this.isEdit
            ? qtyLine.Matrix?.[sheetKey]
            : qtyLine?.[sheetKey] || 1,
          ...pick(matrix, Object.keys(defaultData)),
          ...stub,
          ...districtInfo,
        }
      }
      const quantities = useDefaultQty && !this.orderItem
        ? [defaultData]
        : this.matrixes?.length || this.orderItemQtys?.length
          ? GenQtys()
          : [defaultData]
      this.$patch({
        loading: false,
        quantities: sortBy(quantities, this.grouping === 'Sheet'
          ? 'Sheet'
          : 'DistrictKey'),
      })
    },
    setOptions(item = null as any) {
      const scannable = [item?.ItemType, this.product?.ItemType].includes('Deck')
        ? false
        : Object.hasOwn(item || {}, 'Scannable')
          ? item.Scannable
          : true
      this.$patch({
        options: {
          backside: item?.Backside || false,
          ballotCreation: item?.BallotCreation || false,
          ballotType: item?.BallotType || null,
          language: item?.Language || null,
          scannable,
          size: item?.Size || null,
          stub: item?.Stub || false,
          testApproval: false,
        },
      })
    },
    async startOrder(election: any, item = null as any, order = { orderId: null, jobNumber: '' }) {
      const customerParams = (useAuthStore() as any).user?.SelectedCustomerParameters || {}
      this.$patch({
        defaultBackside: customerParams?.Portal?.backsideText,
      })
      const matrixes = await api.service('pge/electionMatrixes').find({
        query: {
          Active: true,
          Consolidated: false,
          CustomerAccount: election.CustomerAccount,
          Type: election.Type,
          $or: [
            { ElectionID: election.ID },
            { ElectionID: null },
          ],
          $sort: {
            Party: 1,
            DistrictName: 1,
            Subdivision: 1,
            ElectionDistrict: 1,
          },
        },
      })
      const defaultMatrix = matrixes.data.filter((v: any) => v.ElectionID !== election.ID)
      const electionMatrix = matrixes.data.filter((v: any) => v.ElectionID === election.ID)

      const orderQty = item
        ? await api.service('pge/orderQtys').find({
            query: {
              Matrix: true,
              OrderProductID: item.ID,
            },
          })
        : { data: null }

      this.setOptions(item)
      this.$patch({
        activeTab: item ? 'quantity' : 'product',
        backsideText: {
          line1: '',
          line2: '',
          line3: '',
          line4: '',
          line5: '',
          voterFriendly: false,
          ...(item
            ? item?.BacksideText || {}
            : this.defaultBackside),
        },
        election,
        grouping: item?.BySheet ? 'Sheet' : null,
        matrixes: electionMatrix.length > 0
          ? electionMatrix
          : defaultMatrix,
        ...order,
        orderItem: item,
        orderItemQtys: orderQty.data,
        party: item?.Party || undefined,
        product: item?.Product || undefined,
        stub: {
          confirm: item?.Stub || false,
          index: 0,
          ...customerParams?.Portal?.stub || { checkbox1: null, checkbox2: null },
        },
      } as any)

      if (item)
        this.setQuantities()
    },
    async updateProduct(cart: boolean) {
      const { orderId, cartDrawer } = storeToRefs(useCartStore())
      const id = cart ? orderId.value : this.orderId as number

      const productPayload = this.genProductPayload(id as number, true)

      const errorMsg = `Error updating ${cart ? 'cart' : 'product'}`
      const successMsg = `${cart ? 'Cart' : 'Product'} updated`
      this.$patch({ loading: true })
      await snackbarAction(async () => {
        await api.service('pge/orderProducts').patch(this.orderItem.ID, productPayload)
        // set product id on qtys
        const quantityPayload = await this.genQuantityPayload(this.orderItem.ID)
        await api.service('pge/orderQtys').remove(null, {
          query: { OrderProductID: this.orderItem.ID },
        })
        await api.service('pge/orderQtys').create(quantityPayload)

        this.resetOrder()
        if (cart)
          cartDrawer.value = true
        else await api.service('cc/epmsComponent').patch(this.orderItem.ID.toString(), {} as any)
      }, successMsg, errorMsg)
    },
  },
})
